/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { AnimatePresence, Variants, motion, useInView } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWindowSize } from '../../../helpers';
import { Expand, Mute, Play, Shrink, Unmuted } from '../../Icons';
import { Button, Dots, Slidernation } from '../../UI';
import InfiniteSlideShow from '../../UI/InfiniteSlideShow';
import { ReelsMediaProvider, useReelsMedia } from './context';
import useStyle from './style';

export interface ReelType {
  thumbnail: string;
  videoUrl: string;
}

const defaultReels: ReelType[] = [
  {
    thumbnail:
      'https://www.datocms-assets.com/10154/1693241965-what-s-in-my-cove-system-thumbnail.png',
    videoUrl:
      'https://www.datocms-assets.com/10154/1693241969-what-s-in-my-cove-system.mp4',
  },
  {
    thumbnail:
      'https://www.datocms-assets.com/10154/1700240950-protect-your-home-today.png',
    videoUrl:
      'https://www.datocms-assets.com/10154/1693241987-i-love-my-system.mp4',
  },
  {
    thumbnail:
      'https://www.datocms-assets.com/10154/1693241960-4-places-to-use-a-motion-sensor-thumbnail.png',
    videoUrl:
      'https://www.datocms-assets.com/10154/1693242027-4-places-to-use-a-motion-sensor.mp4',
  },
  {
    thumbnail:
      'https://www.datocms-assets.com/10154/1693241951-get-to-know-the-glass-break-sensor-thumbnail.png',
    videoUrl:
      'https://www.datocms-assets.com/10154/1693241996-get-to-know-the-glass-break-sensor.mp4',
  },
  {
    thumbnail:
      'https://www.datocms-assets.com/10154/1683153190-thumbnail-for-flood-sensor-reel.png',
    videoUrl:
      'https://www.datocms-assets.com/10154/1683144755-4-places-to-use-a-flood-sensor.mp4',
  },
];

const slideShowConfigs = {
  xsmall: {
    gap: 15,
    padding: 0,
    slidesToShow: 1,
    slideCreep: 25,
    slideCreepLeft: 15,
  },
  small: {
    gap: 25,
    padding: 0,
    slidesToShow: 1,
    slideCreep: 175,
    slideCreepLeft: undefined,
  },
  medium: {
    gap: 50,
    padding: 0,
    slidesToShow: 3,
    slideCreep: 0,
    slideCreepLeft: undefined,
  },
  large: {
    gap: 50,
    padding: 0,
    slidesToShow: 5,
    slideCreep: 0,
    slideCreepLeft: undefined,
  },
};

interface ReelsProps {
  variant?: 'base' | 'quincy';
  reels?: ReelType[];
  ctaLink?: string;
}

const Reels: React.FC<ReelsProps> = ({
  variant = 'base',
  reels = defaultReels,
  ctaLink = '/quiz',
}) => {
  const { width = 0 } = useWindowSize();
  const getSlideShowConfig = () => {
    if (width >= 1440) {
      return 'large';
    }
    if (width < 1440 && width >= 900) {
      return 'medium';
    }
    if (width < 900 && width >= 675) {
      return 'small';
    }
    return 'xsmall';
  };

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const { expanded, middleSlide, setMiddleSlide } = useReelsMedia();
  const isQuincy = variant === 'quincy';
  const {
    wrapper,
    quincyWrapper,
    slideshowWrapper,
    slideStyle,
    slidernationStyle,
    ctaWrapper,
    backdrop,
  } = useStyle();

  const renderedReels = useMemo(
    () =>
      reels.map(({ thumbnail, videoUrl }, i) => (
        <Reel
          reelNumber={i}
          thumbnail={thumbnail}
          videoLink={videoUrl}
          key={`reel-${i}`}
        />
      )),
    [reels, middleSlide]
  );

  const slideShowConfig = slideShowConfigs[getSlideShowConfig()];

  return (
    <section css={[wrapper, isQuincy && quincyWrapper]} ref={ref}>
      <h2 className="text-h2 text-center">See the Cove system in action.</h2>
      {!isQuincy && (
        <p className="text-center">
          Here are some of the many ways you can protect your home with Cove.
        </p>
      )}
      <div css={slideshowWrapper}>
        {isInView && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: expanded ? 0.75 : 0 }}
              exit={{ opacity: 0 }}
              css={backdrop}
            ></motion.div>
            <InfiniteSlideShow
              {...slideShowConfig}
              currentSlide={
                middleSlide ?? Math.floor(slideShowConfig.slidesToShow / 2)
              }
              showXGap={false}
              onSlideChange={({ middle }) => {
                setMiddleSlide(
                  middle ?? Math.floor(slideShowConfig.slidesToShow / 2)
                );
              }}
              fade
              fadeStyles={css`
                opacity: ${expanded ? '0' : '1'};
                transition: opacity 0.3s ease;

                @media (max-width: ${`${675}px`}) {
                  display: none;
                }
              `}
              leftArrowStyles={css`
                left: ${expanded ? '50%' : '50px'};
                transform: ${expanded ? 'translateX(-335px)' : ''};

                @media (max-width: ${`${675}px`}) {
                  display: none;
                }
              `}
              rightArrowStyles={css`
                right: ${expanded ? '50%' : '50px'};
                transform: ${expanded ? 'translateX(335px)' : ''};

                @media (max-width: ${`${675}px`}) {
                  display: none;
                }
              `}
              slides={renderedReels}
              slideWrapperStyles={slideStyle}
            />
          </>
        )}
      </div>
      <div css={slidernationStyle}>
        <Slidernation
          slideCount={reels.length}
          activeSlide={middleSlide || 0}
          setSlide={setMiddleSlide}
        />
      </div>
      <div css={ctaWrapper}>
        <Button
          cypressId="reels-quiz-cta"
          size="large"
          color="purple"
          style="container"
          href={ctaLink}
        >
          Get Started
        </Button>
        <p>Try it for 60 days risk-free</p>
      </div>
    </section>
  );
};

const reelVariants: Variants = {
  expanded: ({ width, isMiddle, arWidth, arHeight }) => ({
    maxWidth: `${arWidth - 30}px`,
    minWidth: `${arWidth - 30}px`,
    height: width < 675 ? `${arHeight - 60}px` : '85vh',
    zIndex: '111',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: `translate(${
      width < 675 ? 'calc(-50% + (10px / 2))' : '-50%'
    }, calc(-50% + (10px / 2)))`,
    opacity: isMiddle ? 1 : 0.5,
    overflow: 'hidden',
  }),
  normal: ({ width, isMiddle }) => ({
    maxWidth: width < 675 ? `${320 - 40}px` : `${(480 * 9) / 16}px`,
    minWidth: width < 675 ? `${320 - 40}px` : `${(480 * 9) / 16}px`,
    width: '100%',
    zIndex: '1',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: `translate(-50%, -50%)`,
    opacity: isMiddle ? 1 : 0.5,
  }),
};

const Reel: React.FC<{
  thumbnail: string;
  videoLink: string;
  reelNumber: number;
}> = ({ thumbnail, videoLink, reelNumber, ...props }) => {
  const { reelWrapper, expandedVideoStyle, playButton, videoControls } =
    useStyle();
  const { width = 0, height = 0 } = useWindowSize();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { expanded, muted, setExpanded, setMuted, middleSlide } =
    useReelsMedia();
  const isMiddle = middleSlide === reelNumber;
  const isExpanded = expanded && isMiddle;

  const togglePlayback = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    setMuted(!muted);
  };

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const getAspectRatio = () => {
    const neededHeight = (width * 16) / 9;
    const neededWidth = (height * 9) / 16;
    if (neededHeight > height) {
      return {
        width: neededWidth,
        height: (neededWidth * 16) / 9,
      };
    }

    if (neededWidth > width) {
      return {
        width: (neededHeight * 9) / 16,
        height: neededHeight,
      };
    }
    return {
      width: 0,
      height: 0,
    };
  };

  useEffect(() => {
    let playInterval: ReturnType<typeof setTimeout>;
    if (isMiddle) {
      playInterval = setTimeout(() => {
        if (!isPlaying) {
          togglePlayback();
        }
      }, 1000);
    }

    return () => clearTimeout(playInterval);
  }, [isMiddle]);

  return (
    <motion.div
      custom={{
        width,
        isMiddle,
        arWidth: getAspectRatio().width,
        arHeight: getAspectRatio().height,
      }}
      css={[reelWrapper, isExpanded ? expandedVideoStyle : '']}
      initial="normal"
      animate={isExpanded ? 'expanded' : 'normal'}
      variants={reelVariants}
      style={{
        backgroundImage: `url(${thumbnail})`,
      }}
      {...props}
    >
      <AnimatePresence>
        {isMiddle && (
          <>
            <AnimatePresence>
              {isPlaying && (
                <motion.video
                  key={`reel-${reelNumber}-video`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  loop
                  autoPlay
                  muted={muted}
                  onClick={() => togglePlayback()}
                  onLoadedData={() => setIsLoading(false)}
                  onWaiting={() => setIsLoading(true)}
                >
                  <source src={videoLink} />
                </motion.video>
              )}
            </AnimatePresence>
            <motion.button
              key={`reel-${reelNumber}-play-button`}
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              css={css`
                ${playButton}

                & > svg {
                  color: ${isPlaying ? 'transparent' : 'white'};
                }
              `}
              onClick={() => togglePlayback()}
            >
              <Play width={100} />
            </motion.button>
            {isPlaying && isLoading && (
              <motion.div
                key={`reel-${reelNumber}-loading`}
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                css={css`
                  position: absolute;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  width: 100%;
                  height: 100%;
                `}
              >
                <Dots loading css={''} dotCss={''} dotColor={''} />
              </motion.div>
            )}
            <motion.div
              css={videoControls}
              key={`reel-${reelNumber}-controls`}
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <button onClick={() => toggleMute()}>
                {muted ? (
                  <Mute width={20} style={{ color: 'white' }} />
                ) : (
                  <Unmuted width={20} style={{ color: 'white' }} />
                )}
              </button>
              <button
                onClick={() => {
                  toggleExpand();
                }}
              >
                {expanded ? (
                  <Shrink width={20} style={{ color: 'white' }} />
                ) : (
                  <Expand width={20} style={{ color: 'white' }} />
                )}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FinalReels: React.FC<ReelsProps> = props => {
  return (
    <ReelsMediaProvider>
      <Reels {...props} />
    </ReelsMediaProvider>
  );
};

export default FinalReels;
